/**
 * contentValidators/assessmentChecks.ts
 * Validates assessment-related requirements: solutionSteps, CITO parameters
 * (presence and range), cognitive level per tier band, and plus-level enforcement.
 *
 * CITO ROLLUP (Sprint 6 refactor): all CITO-parameter findings are emitted
 * under the single code `CONTENT_CITO_ISSUE`, with `metadata.reason` carrying
 * the discriminator (missing_difficulty / missing_discrimination /
 * difficulty_out_of_range / difficulty_band_mismatch /
 * discrimination_out_of_range / discrimination_band_mismatch).
 *
 * PLUS-INADEQUATE ROLLUP (Sprint 6 refactor): all Plus-tier inadequacy
 * findings are emitted under the single code `CONTENT_PLUS_INADEQUATE`,
 * with `metadata.reason` carrying the discriminator (cognitive_too_low /
 * difficulty_too_low / missing_steps / few_steps / single_step).
 *
 * In both rollups the kind (coach vs info) per emission stays the same as
 * the legacy codes — only the code-namespace was collapsed. Admin UI groups
 * primary by code, secondary by metadata.reason (see computeCodeFrequencyReport).
 */

import type { ValidationIssue } from '../../issueContract'
import { coach, info } from '../../issueContract'
import {
  getTierBand,
  isPlusTier,
  getPlusRequirements,
  isAssessmentEligibleType,
} from '../../policyEngine'
// C1 (hardening-audit §1): profile-aware cognitive floor SSOT. Replaces the
// inline `module !== 'Topo'` carve-outs below — recall-knowledge modules
// (Topo, SpellingRegel) resolve a 'recall' floor, so the gate demands no
// escalation for them without any module-name special-casing.
import { getCognitiveFloorForBand, type TierBand } from '../../../tierBands'
import {
  detectCognitiveLevel,
  COGNITIVE_LEVEL_RANK,
} from '../../../cognitiveLevel'
import type { Question } from '../../../../content'
import { STAGE } from './shared'
import { SEVERITY } from '../../severityScale'

/**
 * Validate assessment requirements: solutionSteps, CITO params, cognitive level, plus-level.
 */
export function validateAssessmentRequirements(
  question: Question,
): ValidationIssue[] {
  const issues: ValidationIssue[] = []
  const qId = question.id ?? 'UNKNOWN'
  const fId = question.familyId ?? 'UNKNOWN'
  const tier = question.tier ?? 1
  const module = question.module

  // ── solutionSteps check for non-plus tiers ──────────────────────────────

  if (!isPlusTier(tier)) {
    const steps = question.feedback?.solutionSteps
    if (!steps || !Array.isArray(steps) || steps.length === 0) {
      issues.push(
        info(
          'content',
          STAGE,
          'CONTENT_MISSING_SOLUTION_STEPS',
          `Question is missing feedback.solutionSteps — recommended for all tiers`,
          {
            field: 'feedback.solutionSteps',
            questionId: qId,
            familyId: fId,
            severity: SEVERITY.COSMETIC,
            suggestion:
              'Add step-by-step solution explanation to improve didactic quality',
          },
        ),
      )
    }
  }

  // ── citoDifficulty / citoDiscrimination presence check ──────────────────
  // ROLLUP: emits CONTENT_CITO_ISSUE with metadata.reason
  {
    const hasCitoDiff =
      question.citoDifficulty !== undefined && question.citoDifficulty !== null
    const hasCitoDisc =
      (question as Record<string, unknown>).citoDiscrimination !== undefined &&
      (question as Record<string, unknown>).citoDiscrimination !== null
    const isAssessmentType = question.questionType
      ? isAssessmentEligibleType(question.questionType)
      : false

    if (!hasCitoDiff) {
      issues.push(
        isAssessmentType
          ? coach(
              'content',
              STAGE,
              'CONTENT_CITO_ISSUE',
              `Assessment-eligible question is missing citoDifficulty parameter`,
              {
                field: 'citoDifficulty',
                questionId: qId,
                familyId: fId,
                severity: SEVERITY.LOW,
                suggestion:
                  'Add citoDifficulty (0.0-1.0) for calibration and assessment selection',
                metadata: {
                  reason: 'missing_difficulty',
                  citoField: 'citoDifficulty',
                  assessmentEligible: true,
                },
              },
            )
          : info(
              'content',
              STAGE,
              'CONTENT_CITO_ISSUE',
              `Question is missing citoDifficulty parameter`,
              {
                field: 'citoDifficulty',
                questionId: qId,
                familyId: fId,
                severity: SEVERITY.INFO,
                suggestion: 'Add citoDifficulty (0.0-1.0) for calibration',
                metadata: {
                  reason: 'missing_difficulty',
                  citoField: 'citoDifficulty',
                  assessmentEligible: false,
                },
              },
            ),
      )
    }

    if (!hasCitoDisc) {
      issues.push(
        isAssessmentType
          ? coach(
              'content',
              STAGE,
              'CONTENT_CITO_ISSUE',
              `Assessment-eligible question is missing citoDiscrimination parameter`,
              {
                field: 'citoDiscrimination',
                questionId: qId,
                familyId: fId,
                severity: SEVERITY.LOW,
                suggestion:
                  'Add citoDiscrimination (0.0-1.0) for assessment quality gating',
                metadata: {
                  reason: 'missing_discrimination',
                  citoField: 'citoDiscrimination',
                  assessmentEligible: true,
                },
              },
            )
          : info(
              'content',
              STAGE,
              'CONTENT_CITO_ISSUE',
              `Question is missing citoDiscrimination parameter`,
              {
                field: 'citoDiscrimination',
                questionId: qId,
                familyId: fId,
                severity: SEVERITY.INFO,
                suggestion: 'Add citoDiscrimination (0.0-1.0) for calibration',
                metadata: {
                  reason: 'missing_discrimination',
                  citoField: 'citoDiscrimination',
                  assessmentEligible: false,
                },
              },
            ),
      )
    }
  }

  // ── citoDifficulty RANGE validation ─────────────────────────────────────
  // ROLLUP: emits CONTENT_CITO_ISSUE with metadata.reason
  {
    const citoDiff = question.citoDifficulty
    if (citoDiff !== undefined && typeof citoDiff === 'number') {
      // Global range
      if (citoDiff < 0.1 || citoDiff > 0.85) {
        issues.push(
          coach(
            'content',
            STAGE,
            'CONTENT_CITO_ISSUE',
            `citoDifficulty ${citoDiff} outside valid range [0.10, 0.85]`,
            {
              field: 'citoDifficulty',
              expected: '0.10-0.85',
              actual: String(citoDiff),
              questionId: qId,
              familyId: fId,
              severity: SEVERITY.NEAR_MEDIUM,
              metadata: {
                reason: 'difficulty_out_of_range',
                citoField: 'citoDifficulty',
                value: citoDiff,
                min: 0.1,
                max: 0.85,
              },
            },
          ),
        )
      }

      // Tier-band specific ranges
      const band = getTierBand(tier)
      const bandRanges: Record<string, { min: number; max: number }> = {
        basic: { min: 0.15, max: 0.55 },
        intermediate: { min: 0.2, max: 0.6 },
        advanced: { min: 0.25, max: 0.7 },
        expert: { min: 0.3, max: 0.8 },
      }
      const bandRange = bandRanges[band]
      if (bandRange && (citoDiff < bandRange.min || citoDiff > bandRange.max)) {
        issues.push(
          info(
            'content',
            STAGE,
            'CONTENT_CITO_ISSUE',
            `citoDifficulty ${citoDiff} outside recommended range [${bandRange.min}, ${bandRange.max}] for ${band} tier band`,
            {
              field: 'citoDifficulty',
              expected: `${bandRange.min}-${bandRange.max}`,
              actual: String(citoDiff),
              questionId: qId,
              familyId: fId,
              severity: SEVERITY.NEAR_MEDIUM,
              suggestion: `Adjust citoDifficulty to fit the ${band} band range, or verify this difficulty is intentional`,
              metadata: {
                reason: 'difficulty_band_mismatch',
                citoField: 'citoDifficulty',
                value: citoDiff,
                band,
                min: bandRange.min,
                max: bandRange.max,
              },
            },
          ),
        )
      }
    }
  }

  // ── citoDiscrimination RANGE validation ─────────────────────────────────
  // ROLLUP: emits CONTENT_CITO_ISSUE with metadata.reason
  {
    const citoDisc = (question as Record<string, unknown>).citoDiscrimination
    if (citoDisc !== undefined && typeof citoDisc === 'number') {
      if (citoDisc < 0.4 || citoDisc > 0.95) {
        issues.push(
          coach(
            'content',
            STAGE,
            'CONTENT_CITO_ISSUE',
            `citoDiscrimination ${citoDisc} outside valid range [0.40, 0.95]`,
            {
              field: 'citoDiscrimination',
              expected: '0.40-0.95',
              actual: String(citoDisc),
              questionId: qId,
              familyId: fId,
              severity: SEVERITY.LOW,
              metadata: {
                reason: 'discrimination_out_of_range',
                citoField: 'citoDiscrimination',
                value: citoDisc,
                min: 0.4,
                max: 0.95,
              },
            },
          ),
        )
      } else if (citoDisc < 0.6 || citoDisc > 0.85) {
        issues.push(
          info(
            'content',
            STAGE,
            'CONTENT_CITO_ISSUE',
            `citoDiscrimination ${citoDisc} outside recommended range [0.60, 0.85]`,
            {
              field: 'citoDiscrimination',
              expected: '0.60-0.85',
              actual: String(citoDisc),
              questionId: qId,
              familyId: fId,
              severity: SEVERITY.COSMETIC,
              suggestion:
                'Adjust citoDiscrimination to 0.60-0.85 for optimal assessment quality',
              metadata: {
                reason: 'discrimination_band_mismatch',
                citoField: 'citoDiscrimination',
                value: citoDisc,
                min: 0.6,
                max: 0.85,
              },
            },
          ),
        )
      }
    }
  }

  // ── Tier-band cognitive level validation ─────────────────────────────────

  if (!isPlusTier(tier)) {
    const band = getTierBand(tier)
    const cogLevel = detectCognitiveLevel(question)

    if (band === 'basic' && cogLevel === 'analysis') {
      issues.push(
        // C2 (hardening-audit): cognitive-level judgments are pedagogical, not
        // content defects. Domain 'pedagogy' routes this through the FG4
        // per-module enforcement ramp (resolvePedagogicalMode) instead of the
        // permanent-enforce 'content' fall-through.
        coach(
          'pedagogy',
          STAGE,
          'CONTENT_BASIS_COGNITIVE_TOO_HIGH',
          `Basis-tier ${tier} question has "${cogLevel}" cognitive level — expected recall or comprehension for this tier band`,
          {
            field: 'question',
            expected: 'recall or comprehension',
            actual: cogLevel,
            questionId: qId,
            familyId: fId,
            severity: SEVERITY.NEAR_MEDIUM,
            suggestion:
              'Basis-tier questions (groep 6) should focus on recall and comprehension — save analysis-level questions for higher tiers',
          },
        ),
      )
    }

    // C1 (hardening-audit §1): profile-aware exemption. Only flag a recall-level
    // question on the advanced band when the module's cognitive profile actually
    // demands more than recall. recall-knowledge modules (Topo, SpellingRegel)
    // resolve a 'recall' floor here, so they are exempt without a hardcoded
    // `module !== 'Topo'` carve-out.
    if (
      band === 'advanced' &&
      cogLevel === 'recall' &&
      getCognitiveFloorForBand(band as TierBand, module) !== 'recall'
    ) {
      issues.push(
        // C2 (hardening-audit): cognitive judgment → 'pedagogy' so it ramps.
        coach(
          'pedagogy',
          STAGE,
          'CONTENT_EIND_COGNITIVE_TOO_LOW',
          `Eind-tier ${tier} question has only "${cogLevel}" cognitive level — expected application or analysis for this tier band`,
          {
            field: 'question',
            expected: 'application or analysis',
            actual: cogLevel,
            questionId: qId,
            familyId: fId,
            severity: SEVERITY.LOW,
            suggestion:
              'Eind-tier questions (groep 8) should require application or analysis — add calculation steps, comparisons, or reasoning to increase cognitive demand',
          },
        ),
      )
    }
  }

  // ── Plus-level enforcement ──────────────────────────────────────────────

  if (isPlusTier(tier)) {
    // C1 (hardening-audit §1): pass `module` so minCognitiveLevel is resolved
    // profile-aware via the COGNITIVE_FLOOR_BY_PROFILE SSOT (tierBands.ts).
    const plusReqs = getPlusRequirements(tier, module)
    if (plusReqs) {
      // 1. Cognitive level check
      const cogLevel = detectCognitiveLevel(question)
      // GSR-27 (audit 2026): use SSOT COGNITIVE_LEVEL_RANK from cognitiveLevel.ts
      // instead of an inline duplicate. 'unknown' is intentionally NOT in the
      // map — the CONTENT_COGNITIVE_LEVEL_UNKNOWN branch below handles it first,
      // so `undefined` here only occurs in unreachable paths.
      const minCogOrder = COGNITIVE_LEVEL_RANK[plusReqs.minCognitiveLevel]
      const actualCogOrder =
        cogLevel === 'unknown' ? undefined : COGNITIVE_LEVEL_RANK[cogLevel]

      // Ruis-audit v1.6 T2.1 (C6): when detectCognitiveLevel() returns
      // 'unknown' we cannot compare ordinals — emit an info() so admin can
      // triage instead of silently passing the Plus gate.
      // C1 (hardening-audit §1): profile-aware skip. When the resolved floor is
      // 'recall' the gate demands no cognitive escalation, so an undetectable
      // level is not actionable — skip the triage info. This generalizes the
      // former `module !== 'Topo'` carve-out to every recall-knowledge module.
      if (cogLevel === 'unknown' && plusReqs.minCognitiveLevel !== 'recall') {
        issues.push(
          // C2 (hardening-audit): cognitive-detection signal → 'pedagogy' so it
          // ramps with the per-module FG4 enforcement modes.
          info(
            'pedagogy',
            STAGE,
            'CONTENT_COGNITIVE_LEVEL_UNKNOWN',
            `Plus-level tier ${tier}: cognitive level could not be auto-detected — manual review suggested`,
            {
              field: 'question',
              expected: plusReqs.minCognitiveLevel,
              actual: 'unknown',
              questionId: qId,
              familyId: fId,
              severity: SEVERITY.TRACE,
              suggestion:
                'Add explicit Bloom-indicators (bereken/vergelijk/leg uit waarom/redeneer) or set cognitiveLevel manually so the Plus-gate can evaluate this question.',
            },
          ),
        )
      } else if (actualCogOrder !== undefined && actualCogOrder < minCogOrder) {
        // C1 (hardening-audit §1): no `module !== 'Topo'` carve-out needed.
        // recall-knowledge modules resolve minCognitiveLevel='recall' (lowest
        // rank), so actualCogOrder < minCogOrder can never hold for them — the
        // profile-aware floor makes the exemption structural, not name-based.
        issues.push(
          // C2 (hardening-audit): the COGNITIVE branch of CONTENT_PLUS_INADEQUATE
          // is a pedagogical judgment → domain 'pedagogy' so it ramps via the
          // per-module FG4 enforcement modes. NOTE: the cito-difficulty branch
          // below deliberately keeps domain 'content' (it is a difficulty-
          // calibration defect, not a cognitive judgment). The same code thus
          // legitimately appears under two domains depending on branch — admin
          // aggregations group by code first, domain second.
          coach(
            'pedagogy',
            STAGE,
            'CONTENT_PLUS_INADEQUATE',
            `Plus-level tier ${tier} requires ≥${plusReqs.minCognitiveLevel} cognitive level, detected "${cogLevel}"`,
            {
              field: 'question',
              expected: plusReqs.minCognitiveLevel,
              actual: cogLevel,
              questionId: qId,
              familyId: fId,
              severity: SEVERITY.REVIEW,
              suggestion: `Plus questions should require ${plusReqs.minCognitiveLevel}-level thinking: multi-step reasoning, transfer to new contexts, or combining skills — not just recall or simple comprehension`,
              metadata: {
                reason: 'cognitive_too_low',
                tier,
                expected: plusReqs.minCognitiveLevel,
                actual: cogLevel,
              },
            },
          ),
        )
      }

      // 2. citoDifficulty minimum check
      const citoDiff = question.citoDifficulty
      if (citoDiff !== undefined && typeof citoDiff === 'number') {
        if (citoDiff < plusReqs.minCitoDifficulty) {
          issues.push(
            // C2 (hardening-audit): DELIBERATELY domain 'content'. This is a
            // difficulty-calibration defect (citoDifficulty too low), NOT a
            // cognitive judgment, so it stays in the content lane and does NOT
            // route through the pedagogy FG4 ramp — unlike its cognitive
            // sibling above (metadata.reason 'cognitive_too_low').
            coach(
              'content',
              STAGE,
              'CONTENT_PLUS_INADEQUATE',
              `Plus-level tier ${tier} requires citoDifficulty ≥${plusReqs.minCitoDifficulty}, got ${citoDiff}`,
              {
                field: 'citoDifficulty',
                expected: `≥${plusReqs.minCitoDifficulty}`,
                actual: String(citoDiff),
                questionId: qId,
                familyId: fId,
                severity: SEVERITY.NEAR_MEDIUM,
                suggestion: `Plus questions should be harder than base-level. Increase complexity: use larger numbers, multi-step problems, unfamiliar contexts, or combined skills`,
                metadata: {
                  reason: 'difficulty_too_low',
                  tier,
                  value: citoDiff,
                  min: plusReqs.minCitoDifficulty,
                },
              },
            ),
          )
        }
      }

      // 3. solutionSteps requirement
      if (plusReqs.requireSolutionSteps) {
        const steps = question.feedback?.solutionSteps
        if (!steps || !Array.isArray(steps) || steps.length === 0) {
          issues.push(
            coach(
              'content',
              STAGE,
              'CONTENT_PLUS_INADEQUATE',
              `Plus-level tier ${tier} requires solutionSteps in feedback`,
              {
                field: 'feedback.solutionSteps',
                questionId: qId,
                familyId: fId,
                severity: SEVERITY.MEDIUM,
                suggestion:
                  'Plus questions must have step-by-step solution explanations to support the higher cognitive demand',
                metadata: {
                  reason: 'missing_steps',
                  tier,
                },
              },
            ),
          )
        } else if (steps.length < plusReqs.minSolutionSteps) {
          issues.push(
            info(
              'content',
              STAGE,
              'CONTENT_PLUS_INADEQUATE',
              `Plus-level tier ${tier} recommends ≥${plusReqs.minSolutionSteps} solution steps, got ${steps.length}`,
              {
                field: 'feedback.solutionSteps',
                expected: `≥${plusReqs.minSolutionSteps}`,
                actual: String(steps.length),
                questionId: qId,
                familyId: fId,
                severity: SEVERITY.NEAR_MINOR,
                metadata: {
                  reason: 'few_steps',
                  tier,
                  count: steps.length,
                  min: plusReqs.minSolutionSteps,
                },
              },
            ),
          )
        }
      }

      // 4. Multi-step reasoning indicator
      if (plusReqs.requireMultiStep) {
        const qText = question.question ?? ''
        const MULTI_STEP_INDICATORS =
          /eerst.*dan|stap.*stap|vervolgens|daarna|combineer|vergelijk.*en|bereken.*en.*bereken|twee.*stappen|meerdere/i
        const HAS_MULTI_OPERATION = /\d+.*[+\-×÷].*\d+.*[+\-×÷].*\d+/
        const isMultiStep =
          MULTI_STEP_INDICATORS.test(qText) ||
          HAS_MULTI_OPERATION.test(qText) ||
          (question.feedback?.solutionSteps?.length ?? 0) >= 3

        if (!isMultiStep) {
          issues.push(
            info(
              'content',
              STAGE,
              'CONTENT_PLUS_INADEQUATE',
              `Plus-level tier ${tier} expects multi-step or transfer questions, but this appears to be single-step`,
              {
                field: 'question',
                questionId: qId,
                familyId: fId,
                severity: SEVERITY.NEAR_LOW,
                suggestion:
                  'Plus questions should involve: multi-step reasoning, transfer to unfamiliar contexts, combining multiple skills, or analysis/comparison — not just a harder version of the same procedure',
                metadata: {
                  reason: 'single_step',
                  tier,
                },
              },
            ),
          )
        }
      }
    }
  }

  return issues
}
